import { c as _c } from "react/compiler-runtime";
import { cx } from '@7f-ui';
import { calendarDateToDate, toCalendarDate, toRangeValue } from '@7f-ui/components/datepicker/core/to-calendar-date';
import { DateRangePicker } from '@7f-ui/components/datepicker/date-range-picker/date-range-picker';
import { MessageBox } from '@7f-ui/components/message-box/message-box';
import { textFieldVariants } from '@7f-ui/components/text-field/text-field';
import { Panel } from '@feat/feat-search/panel';
import { trackWebsiteMessage } from '@feat/feat-tracking/search';
import { Field, Label } from '@headlessui/react';
import { createCalendar } from '@internationalized/date';
import { CalendarBlankIcon } from '@phosphor-icons/react';
import { GERMAN_LOCALE } from '@utils/utils-format';
import { isWithinInterval } from 'date-fns';
import type { MutableRefObject } from 'react';
import { Fragment, useRef, useState } from 'react';
import type { AriaDatePickerProps, DateValue } from 'react-aria';
import { useDateField, useDateRangePicker, useDateSegment } from 'react-aria';
import type { DateFieldState, DateRangePickerState } from 'react-stately';
import { type DateSegment as DateSegmentType, useDateFieldState, useDateRangePickerState } from 'react-stately';
import { completeDateRange } from '../model/date-range';
type DateRangeFieldProps = {
  label: string;
  value: [Date | null, Date | null] | null;
  min: Date;
  max: Date;
  isActive: boolean;
  error?: boolean;
  onFocus?: () => void;
  onLabelClick?: () => void;
  onChange?: (value: [Date | null, Date | null]) => void;
  onReset?: () => void;
};
export function DateRangeField(t0) {
  const $ = _c(77);
  let error;
  let onChange;
  let props;
  let value;
  if ($[0] !== t0) {
    ({
      onChange,
      value,
      error,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = error;
    $[2] = onChange;
    $[3] = props;
    $[4] = value;
  } else {
    error = $[1];
    onChange = $[2];
    props = $[3];
    value = $[4];
  }
  const [refreshDatePicker, setRefreshDatePicker] = useState(0);
  const t1 = value ?? undefined;
  let t2;
  if ($[5] !== t1) {
    t2 = toRangeValue(t1);
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== props.min) {
    t3 = toCalendarDate(props.min);
    $[7] = props.min;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== props.max) {
    t4 = toCalendarDate(props.max);
    $[9] = props.max;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== onChange) {
    t5 = range => {
      if (!range) {
        return;
      }
      const start = calendarDateToDate(range.start);
      const end = calendarDateToDate(range.end);
      onChange?.([start, end]);
      setRefreshDatePicker(_temp);
    };
    $[11] = onChange;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== props || $[14] !== t2 || $[15] !== t3 || $[16] !== t4 || $[17] !== t5) {
    t6 = {
      ...props,
      value: t2,
      minValue: t3,
      maxValue: t4,
      onChange: t5
    };
    $[13] = props;
    $[14] = t2;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const state = useDateRangePickerState(t6);
  const ref = useRef(null);
  const {
    labelProps,
    groupProps,
    startFieldProps,
    endFieldProps
  } = useDateRangePicker(props, state, ref);
  let T0;
  let isInvalid;
  let t10;
  let t11;
  let t12;
  let t13;
  let t7;
  let t8;
  let t9;
  if ($[19] !== error || $[20] !== groupProps || $[21] !== labelProps || $[22] !== props.label || $[23] !== props.onLabelClick || $[24] !== state) {
    const {
      label,
      input
    } = textFieldVariants({
      inputSpacing: false
    });
    let t14;
    if ($[34] !== error || $[35] !== state) {
      t14 = error || !isValid(state);
      $[34] = error;
      $[35] = state;
      $[36] = t14;
    } else {
      t14 = $[36];
    }
    isInvalid = t14;
    T0 = Field;
    t12 = Fragment;
    let t15;
    if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = <CalendarBlankIcon size={24} weight="bold" />;
      $[37] = t15;
    } else {
      t15 = $[37];
    }
    if ($[38] !== props.onLabelClick) {
      t13 = <Label className="dates-label grid size-12 cursor-pointer place-content-center text-secondary-text" onClick={props.onLabelClick}>{t15}</Label>;
      $[38] = props.onLabelClick;
      $[39] = t13;
    } else {
      t13 = $[39];
    }
    t10 = "dates-input relative flex flex-1";
    t11 = <Label {...labelProps} className={label()}>{props.label}</Label>;
    t7 = groupProps;
    t8 = ref;
    t9 = cx(input(), "flex h-12", {
      "text-error": isInvalid
    });
    $[19] = error;
    $[20] = groupProps;
    $[21] = labelProps;
    $[22] = props.label;
    $[23] = props.onLabelClick;
    $[24] = state;
    $[25] = T0;
    $[26] = isInvalid;
    $[27] = t10;
    $[28] = t11;
    $[29] = t12;
    $[30] = t13;
    $[31] = t7;
    $[32] = t8;
    $[33] = t9;
  } else {
    T0 = $[25];
    isInvalid = $[26];
    t10 = $[27];
    t11 = $[28];
    t12 = $[29];
    t13 = $[30];
    t7 = $[31];
    t8 = $[32];
    t9 = $[33];
  }
  let t14;
  if ($[40] !== startFieldProps) {
    t14 = <DateField {...startFieldProps} />;
    $[40] = startFieldProps;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  let t15;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <span aria-hidden="true" className="px-1">–</span>;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  let t16;
  if ($[43] !== endFieldProps) {
    t16 = <DateField {...endFieldProps} />;
    $[43] = endFieldProps;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  let t17;
  if ($[45] !== t14 || $[46] !== t16 || $[47] !== t7 || $[48] !== t8 || $[49] !== t9) {
    t17 = <div {...t7} ref={t8} className={t9}>{t14}{t15}{t16}</div>;
    $[45] = t14;
    $[46] = t16;
    $[47] = t7;
    $[48] = t8;
    $[49] = t9;
    $[50] = t17;
  } else {
    t17 = $[50];
  }
  let t18;
  if ($[51] !== t10 || $[52] !== t11 || $[53] !== t17) {
    t18 = <div className={t10}>{t11}{t17}</div>;
    $[51] = t10;
    $[52] = t11;
    $[53] = t17;
    $[54] = t18;
  } else {
    t18 = $[54];
  }
  let t19;
  if ($[55] !== T0 || $[56] !== t12 || $[57] !== t13 || $[58] !== t18) {
    t19 = <T0 as={t12}>{t13}{t18}</T0>;
    $[55] = T0;
    $[56] = t12;
    $[57] = t13;
    $[58] = t18;
    $[59] = t19;
  } else {
    t19 = $[59];
  }
  const t20 = !props.isActive;
  let t21;
  if ($[60] !== props.isActive || $[61] !== t20) {
    t21 = cx({
      hidden: t20,
      "left-1/2 min-w-176 max-w-176 -translate-x-1/2 md:left-0 md:translate-x-0": props.isActive
    });
    $[60] = props.isActive;
    $[61] = t20;
    $[62] = t21;
  } else {
    t21 = $[62];
  }
  let t22;
  if ($[63] !== isInvalid || $[64] !== onChange || $[65] !== props.isActive || $[66] !== props.max || $[67] !== props.min || $[68] !== refreshDatePicker || $[69] !== value) {
    t22 = props.isActive && <div className="grid gap-4"><DateRangePicker key={refreshDatePicker} value={value} min={props.min} max={props.max} onChange={range_0 => {
        const dates = completeDateRange(range_0);
        if (dates) {
          onChange?.(dates);
          trackWebsiteMessage("search", {
            from: dates[0],
            to: dates[1]
          });
        }
      }} />{isInvalid && <MessageBox type="error">Bitte wähle einen gültigen Reisezeitraum.</MessageBox>}</div>;
    $[63] = isInvalid;
    $[64] = onChange;
    $[65] = props.isActive;
    $[66] = props.max;
    $[67] = props.min;
    $[68] = refreshDatePicker;
    $[69] = value;
    $[70] = t22;
  } else {
    t22 = $[70];
  }
  let t23;
  if ($[71] !== t21 || $[72] !== t22) {
    t23 = <Panel className={t21}>{t22}</Panel>;
    $[71] = t21;
    $[72] = t22;
    $[73] = t23;
  } else {
    t23 = $[73];
  }
  let t24;
  if ($[74] !== t19 || $[75] !== t23) {
    t24 = <>{t19}{t23}</>;
    $[74] = t19;
    $[75] = t23;
    $[76] = t24;
  } else {
    t24 = $[76];
  }
  return t24;
}
function _temp(current) {
  return current + 1;
}
function DateField(props) {
  const $ = _c(8);
  const ref = useRef(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = toCalendarDate(new Date(2024, 0, 31));
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props) {
    t1 = {
      ...props,
      placeholderValue: t0,
      locale: GERMAN_LOCALE,
      createCalendar
    };
    $[1] = props;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const state = useDateFieldState(t1);
  const {
    fieldProps
  } = useDateField(props, state, ref);
  let t2;
  if ($[3] !== state) {
    t2 = state.segments.map((segment, index) => <DateSegment key={index} segment={segment} state={state} />);
    $[3] = state;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== fieldProps || $[6] !== t2) {
    t3 = <div {...fieldProps} ref={ref} className="flex">{t2}</div>;
    $[5] = fieldProps;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
type DateSegmentProps = {
  segment: DateSegmentType;
  state: DateFieldState;
  segmentRef?: MutableRefObject<HTMLElement>;
};
function DateSegment(t0) {
  const $ = _c(11);
  const {
    segment,
    state
  } = t0;
  const ref = useRef(null);
  const {
    segmentProps
  } = useDateSegment(segment, state, ref);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = cx(SEGMENT_CLASSNAME, "group rounded-xs outline-hidden focus:bg-primary focus:text-white");
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = segment.isPlaceholder ? undefined : "none";
  const t3 = segment.isPlaceholder ? "" : 0;
  let t4;
  if ($[1] !== t2 || $[2] !== t3) {
    t4 = {
      display: t2,
      height: t3,
      pointerEvents: "none"
    };
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== segment.placeholder || $[5] !== t4) {
    t5 = <span aria-hidden="true" className="block w-full text-center text-gray-500 uppercase group-focus:text-white" style={t4}>{segment.placeholder}</span>;
    $[4] = segment.placeholder;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const t6 = segment.isPlaceholder ? "" : segment.text;
  let t7;
  if ($[7] !== segmentProps || $[8] !== t5 || $[9] !== t6) {
    t7 = <div className="max-h-7"><div {...segmentProps} ref={ref} className={t1}>{t5}{t6}</div></div>;
    $[7] = segmentProps;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  return t7;
}
const isValid = ({
  value,
  isInvalid
}: DateRangePickerState) => {
  if (!value?.start || !value?.end) return true;
  if (value.start.year.toString().length < 4 || value.end.year.toString().length < 4) return true;
  return !isInvalid;
};
const SEGMENT_CLASSNAME = 'date-segment';
export const focusDateField = () => {
  const segment = document.querySelector<HTMLElement>(`.${SEGMENT_CLASSNAME}`);
  segment?.focus();
};
export const validateDateRange = (dates: [Date | null, Date | null] | null, {
  start,
  end
}: {
  start: Date;
  end: Date;
}) => {
  const [from, to] = dates ?? [];
  if (!from || !to) return false;
  const validInterval = {
    start,
    end
  };
  return isWithinInterval(from, validInterval) && isWithinInterval(to, validInterval);
};